/* 主组件：日麻点数计算器 */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

const T = window.MJ;
const Tile = window.Tile;

// ============= 主题 =============
const themes = {
  wafu: {
    name: '和风',
    bg: '#f3ede0',
    panel: '#faf6ec',
    panel2: '#ece5d3',
    ink: '#1d1815',
    ink2: '#5a5046',
    line: '#cfc4a8',
    accent: '#a93226',
    accentSoft: '#c1574c',
    gold: '#9a7d2e',
    surface: '#fffdf6',
    keyboardBg: '#ede5d0',
  },
  yajou: {
    name: '雀庄夜场',
    bg: '#0d2419',
    panel: '#143626',
    panel2: '#1a4231',
    ink: '#f3ecd2',
    ink2: '#a89e7d',
    line: '#2d5240',
    accent: '#d4af37',
    accentSoft: '#b89a30',
    gold: '#e8c451',
    surface: '#0f2c1e',
    keyboardBg: '#103024',
  },
  mono: {
    name: '极简',
    bg: '#f6f6f4',
    panel: '#ffffff',
    panel2: '#eeeeec',
    ink: '#1a1a1a',
    ink2: '#5e5e5e',
    line: '#dddddc',
    accent: '#1a1a1a',
    accentSoft: '#3a3a3a',
    gold: '#4a4a4a',
    surface: '#ffffff',
    keyboardBg: '#eeeeec',
  }
};

// ============= 样式工厂 =============
function makeStyles(th) {
  return {
    app: {
      width: '100%',
      maxWidth: 420,
      margin: '0 auto',
      minHeight: '100vh',
      background: th.bg,
      color: th.ink,
      fontFamily: '"PingFang SC","Hiragino Mincho ProN","Songti SC",ui-serif,serif',
      paddingBottom: 280,
      position: 'relative',
      overflow: 'hidden',
    },
    header: {
      padding: '14px 16px 8px',
      borderBottom: `1px solid ${th.line}`,
      background: th.panel,
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'
    },
    title: { fontSize: 18, fontWeight: 600, letterSpacing: '0.04em', color: th.ink },
    subtitle: { fontSize: 11, color: th.ink2, letterSpacing: '0.2em' },
    sectionLabel: {
      fontSize: 10, color: th.ink2, letterSpacing: '0.3em',
      padding: '10px 16px 4px', display: 'flex', alignItems: 'center', gap: 8
    },
    chipRow: { display: 'flex', gap: 6, padding: '6px 12px', flexWrap: 'wrap' },
    chip: (active) => ({
      padding: '6px 10px',
      fontSize: 12, fontWeight: 500,
      borderRadius: 4,
      border: `1px solid ${active ? th.accent : th.line}`,
      background: active ? th.accent : th.panel,
      color: active ? '#fff' : th.ink2,
      transition: 'all 120ms',
      cursor: 'pointer',
      letterSpacing: '0.05em',
      lineHeight: 1.2,
    }),
    handTray: {
      margin: '6px 12px',
      padding: '12px 10px 10px',
      background: th.surface,
      border: `1px solid ${th.line}`,
      borderRadius: 6,
      minHeight: 78,
      position: 'relative',
    },
    handLabel: {
      position: 'absolute', top: -8, left: 12, padding: '0 6px',
      background: th.bg, fontSize: 10, color: th.ink2, letterSpacing: '0.2em'
    },
    keyboard: {
      position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
      width: '100%', maxWidth: 420,
      background: th.keyboardBg,
      borderTop: `1px solid ${th.line}`,
      padding: '8px 6px 12px',
      zIndex: 5,
      boxShadow: '0 -8px 24px rgba(0,0,0,.06)',
    }
  };
}

// ============= Tab 切换栏 =============
function TabBar({ th, activeTab, onSwitch }) {
  return (
    <div style={{ display: 'flex', background: th.panel, borderBottom: `1px solid ${th.line}` }}>
      {[['quick', '快速计算'], ['tile', '摆牌计算']].map(([k, v]) => (
        <button key={k} onClick={() => onSwitch(k)}
          style={{
            flex: 1, padding: '10px 0', fontSize: 13,
            border: 'none',
            borderBottom: `2px solid ${activeTab === k ? th.accent : 'transparent'}`,
            background: 'transparent',
            color: activeTab === k ? th.accent : th.ink2,
            cursor: 'pointer',
            fontWeight: activeTab === k ? 600 : 400,
            letterSpacing: '0.1em',
            fontFamily: '"PingFang SC","Hiragino Mincho ProN","Songti SC",ui-serif,serif',
            transition: 'color 150ms, border-color 150ms',
          }}>{v}</button>
      ))}
    </div>
  );
}

// ============= 主组件 =============
function App() {
  // ----- tweaks（主题 + 设置）-----
  const [tweaks, setTweaks] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('mj-tweaks') || '{}');
      return { theme: 'wafu', showFuDetail: true, ...saved };
    } catch { return { theme: 'wafu', showFuDetail: true }; }
  });
  const setTweak = (key, val) => {
    setTweaks(prev => {
      const next = { ...prev, [key]: val };
      try { localStorage.setItem('mj-tweaks', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // ----- 当前 tab -----
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem('mj-active-tab') || 'quick'; }
    catch { return 'quick'; }
  });
  const switchTab = (tab) => {
    setActiveTab(tab);
    try { localStorage.setItem('mj-active-tab', tab); } catch {}
  };

  const th = themes[tweaks.theme] || themes.wafu;
  const s = useMemo(() => makeStyles(th), [th]);

  // ----- 摆牌计算器状态 -----
  const [hand, setHand] = useState([]);
  const [agariTile, setAgariTile] = useState(null);
  const [melds, setMelds] = useState([]);
  const [doraInds, setDoraInds] = useState([]);
  const [uraInds, setUraInds] = useState([]);
  const [focus, setFocus] = useState('hand');
  const [meldDraft, setMeldDraft] = useState(null);

  // 局况
  const [roundWind, setRoundWind] = useState('1z');
  const [seatWind, setSeatWind] = useState('1z');
  const [honba, setHonba] = useState(0);
  const [deposit, setDeposit] = useState(0);
  const [by, setBy] = useState('ron');

  // 状态役
  const [riichi, setRiichi] = useState(false);
  const [doubleRiichi, setDoubleRiichi] = useState(false);
  const [ippatsu, setIppatsu] = useState(false);
  const [special, setSpecial] = useState(null);

  // 历史
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mj-history') || '[]'); }
    catch { return []; }
  });
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // ----- 选牌 -----
  const addTile = (t) => {
    if (focus === 'hand') {
      const usedByMelds = melds.reduce((sum, m) => sum + (m.type === 'minkan' || m.type === 'ankan' ? 4 : 3), 0);
      const max = 14 - usedByMelds;
      if (hand.length >= max) return;
      const next = T.sortTiles([...hand, t]);
      setHand(next);
      if (next.length === max) setAgariTile(t);
    } else if (focus === 'dora') {
      if (doraInds.length < 5) setDoraInds([...doraInds, t]);
    } else if (focus === 'ura') {
      if (uraInds.length < 5) setUraInds([...uraInds, t]);
    } else if (focus.startsWith('meld:') && meldDraft) {
      if (meldDraft.type === 'chii' && meldDraft.tiles.length === 0) {
        const n = T.numOf(t);
        const su = T.suitOf(t);
        if (su === 'z' || n > 7) return;
        setMeldDraft({ ...meldDraft, tiles: [t, (n + 1) + su, (n + 2) + su] });
      } else if (meldDraft.type === 'pon') {
        setMeldDraft({ ...meldDraft, tiles: [t, t, t] });
      } else if ((meldDraft.type === 'minkan' || meldDraft.type === 'ankan') && meldDraft.tiles.length === 0) {
        setMeldDraft({ ...meldDraft, tiles: [t, t, t, t] });
      }
    }
  };

  const removeHandTile = (idx) => {
    const t = hand[idx];
    const next = hand.filter((_, i) => i !== idx);
    setHand(next);
    if (agariTile === t && next.indexOf(t) < 0) setAgariTile(null);
  };

  const setAsAgari = (t) => setAgariTile(t);

  const startMeld = (type) => {
    setMeldDraft({ type, tiles: [] });
    setFocus('meld:' + melds.length);
  };
  const confirmMeld = () => {
    if (!meldDraft) return;
    const need = meldDraft.type === 'minkan' || meldDraft.type === 'ankan' ? 4 : 3;
    if (meldDraft.tiles.length !== need) return;
    setMelds([...melds, meldDraft]);
    setMeldDraft(null);
    setFocus('hand');
  };
  const removeMeld = (idx) => setMelds(melds.filter((_, i) => i !== idx));

  const clearAll = () => {
    setHand([]); setAgariTile(null); setMelds([]); setDoraInds([]); setUraInds([]);
    setRiichi(false); setDoubleRiichi(false); setIppatsu(false); setSpecial(null);
    setMeldDraft(null); setFocus('hand');
  };

  // ----- 计算 -----
  const result = useMemo(() => {
    const usedByMelds = melds.reduce((sum, m) => sum + (m.type === 'minkan' || m.type === 'ankan' ? 4 : 3), 0);
    const need = 14 - usedByMelds;
    if (hand.length !== need || !agariTile) return null;
    try {
      return T.calculate({
        handTiles: [...hand, ...melds.flatMap(m => m.tiles)],
        melds,
        agariTile,
        by,
        roundWind, seatWind,
        riichi, doubleRiichi, ippatsu,
        rinshan: special === 'rinshan',
        chankan: special === 'chankan',
        haitei: special === 'haitei',
        hotei: special === 'hotei',
        tenhou: special === 'tenhou',
        chiihou: special === 'chiihou',
        honba, deposit,
        doraIndicators: doraInds,
        uraIndicators: uraInds,
        rule: { kuitanyao: true, akadora: true, kazoeYakuman: true, doubleWindFu: 4 }
      });
    } catch (e) {
      console.error(e);
      return { error: 'calc-error' };
    }
  }, [hand, agariTile, melds, by, roundWind, seatWind, riichi, doubleRiichi, ippatsu, special, honba, deposit, doraInds, uraInds]);

  // ----- 保存历史 -----
  const saveHistory = () => {
    if (!result || result.error) return;
    const entry = {
      ts: Date.now(),
      hand: [...hand], melds: JSON.parse(JSON.stringify(melds)),
      agariTile, by, roundWind, seatWind,
      total: result.total, name: result.name,
      han: result.han, fu: result.fu,
      yaku: result.yaku, yakuman: result.yakuman
    };
    const next = [entry, ...history].slice(0, 30);
    setHistory(next);
    try { localStorage.setItem('mj-history', JSON.stringify(next)); } catch {}
  };

  // ----- 渲染 -----
  const paddingBottom = activeTab === 'quick' ? 90 : 280;

  return (
    <div style={{ ...s.app, paddingBottom }}>
      <Header th={th} s={s}
        onHistory={() => setShowHistory(true)}
        onSettings={() => setShowSettings(true)}
        onClear={activeTab === 'tile' ? clearAll : null}
      />

      <TabBar th={th} activeTab={activeTab} onSwitch={switchTab} />

      {activeTab === 'quick' ? (
        <QuickCalcPane th={th} s={s} />
      ) : (
        <>
          <TableSection th={th} s={s}
            roundWind={roundWind} setRoundWind={setRoundWind}
            seatWind={seatWind} setSeatWind={setSeatWind}
            honba={honba} setHonba={setHonba}
            deposit={deposit} setDeposit={setDeposit}
          />

          <DoraSection th={th} s={s}
            doraInds={doraInds} setDoraInds={setDoraInds}
            uraInds={uraInds} setUraInds={setUraInds}
            focus={focus} setFocus={setFocus}
            riichi={riichi || doubleRiichi}
          />

          <HandSection th={th} s={s}
            hand={hand} agariTile={agariTile} melds={melds} meldDraft={meldDraft}
            focus={focus} setFocus={setFocus}
            removeHandTile={removeHandTile}
            setAsAgari={setAsAgari}
            removeMeld={removeMeld}
            confirmMeld={confirmMeld}
            startMeld={startMeld}
          />

          <FlagsSection th={th} s={s}
            by={by} setBy={setBy}
            riichi={riichi} setRiichi={setRiichi}
            doubleRiichi={doubleRiichi} setDoubleRiichi={setDoubleRiichi}
            ippatsu={ippatsu} setIppatsu={setIppatsu}
            special={special} setSpecial={setSpecial}
          />

          <ResultSection th={th} s={s} result={result}
            seatWind={seatWind} by={by}
            showFuDetail={tweaks.showFuDetail}
            onSave={saveHistory}
          />

          <Keyboard th={th} s={s} onPick={addTile} focus={focus} />
        </>
      )}

      {showHistory && (
        <HistoryDrawer th={th} history={history} onClose={() => setShowHistory(false)}
          onClear={() => {
            setHistory([]);
            try { localStorage.removeItem('mj-history'); } catch {}
          }} />
      )}
      {showSettings && (
        <SettingsDrawer th={th} onClose={() => setShowSettings(false)}
          tweaks={tweaks} setTweak={setTweak} />
      )}
    </div>
  );
}

window.MahjongApp = App;
